import { useState, useMemo, useCallback } from "react";
import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "core/components/Button";
import Field from "core/components/Field";
import Flex from "core/components/Flex";
import {
  Panel,
  PanelContent,
  PanelDescription,
  PanelHeader,
  PanelTitle,
} from "core/components/Panel";
import Typography from "core/components/Typography";
import {
  useUserProfilesQuery,
  useOrganizationByIdQuery,
  useMembershipListQuery,
} from "core/queries";
import { createMemberships, createUserProfiles } from "modules/admin/actions";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import Combobox from "core/components/Field/Combobox";
import {
  faSearch,
  faPaperPlane,
  faEnvelope,
  faTrash,
  faClose,
} from "@fortawesome/pro-regular-svg-icons";
import {
  ROLES,
  TABLEAU_ROLES,
  ANALYTICS_HUB_USER_ROLES,
  SYSTEM_ROLE_KEYS,
} from "core/constants";
import { email } from "common/validation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { toast } from "sonner";
import z from "zod";
import Error from "core/components/Error";
import { Tooltip } from "react-tooltip";
import { MembershipCard, MembershipCardError } from "./MembershipCard";

const addMembersSchema = z
  .array(
    z.object({
      userId: z.string().min(1),
      email: z.email(),
      orgId: z.string().min(1),
      role: z.enum(Object.keys(ROLES)),
      ahuRole: z.enum(Object.keys(ANALYTICS_HUB_USER_ROLES)),
      biRole: z.enum(Object.keys(TABLEAU_ROLES)),
      dataAccess: z.boolean().default(false),
      status: z.enum(["success", "error", "none"]).default("none"),
      errorMessage: z.string().nullish(),
    })
  )
  .default([])
  .transform((data) => {
    return data.map((newMember) => {
      return {
        user_id: newMember.userId,
        organization_id: newMember.orgId,
        role: newMember.role,
        analytics_hub_access: newMember.ahuRole,
        direct_data_access_enabled: newMember.dataAccess,
        tableau_config: {
          license: newMember.biRole,
          enabled: newMember.biRole === "none" ? false : true,
        },
      };
    });
  });

const invitedMembersSchema = z
  .array(
    z.object({
      email: z.email(),
      orgId: z.string().min(1),
      role: z.enum(Object.keys(ROLES)),
      ahuRole: z.enum(Object.keys(ANALYTICS_HUB_USER_ROLES)),
      biRole: z.enum(Object.keys(TABLEAU_ROLES)),
      dataAccess: z.boolean().default(false),
      status: z.enum(["success", "error", "none"]).default("none"),
      errorMessage: z.string().nullish(),
    })
  )
  .default([])
  .transform((data) => {
    return data.map((invitedMember) => {
      return {
        email: invitedMember.email,
        memberships: [
          {
            organization_id: invitedMember.orgId,
            role: invitedMember.role,
            analytics_hub_access: invitedMember.ahuRole,
            direct_data_access_enabled: invitedMember.dataAccess,
            tableau_config: {
              license: invitedMember.biRole,
              enabled: invitedMember.biRole === "none" ? false : true,
            },
          },
        ],
      };
    });
  });

const AddMemberPanel = () => {
  const { orgId } = useParams();
  const queryClient = useQueryClient();

  const [isOpen, setOpen] = useState(true);
  const [error, setError] = useState(null);
  const [addedMembers, setAddedMembers] = useState([]);
  const [invitedMembers, setInvitedMembers] = useState([]);
  const parsedMembers = addMembersSchema.safeParse(addedMembers);

  // Fetch org data to get allowed domains
  const { data: orgData } = useOrganizationByIdQuery({
    organizationId: orgId,
  });
  const allowedDomains = useMemo(
    () => orgData?.domain || [],
    [orgData?.domain]
  );

  const { data: userProfiles, isFetching: isFetchingProfiles } =
    useUserProfilesQuery({
      select: (data) => {
        return data.rows;
      },
    });
  const { data: currentOrgMembers, isFetching: isFetchingOrgMembers } =
    useMembershipListQuery({
      orgId,
      select: (data) => {
        return data.rows;
      },
    });

  const userOptions = useMemo(() => {
    // Filter out already-selected member from options
    return (userProfiles || [])
      .filter(
        (profile) =>
          // only enabled user profiles
          profile.enabled &&
          // has org id in assignable list
          profile.assignable_org_ids.includes(orgId) &&
          // only user profiles who are not already org members
          !currentOrgMembers.find(
            (currentMember) => currentMember.user_id === profile.user_id
          ) &&
          // isn't already in the add members list
          !addedMembers.find(
            (addedMember) => addedMember.userId === profile.user_id
          )
      )
      .map((profile) => ({
        label: profile.name,
        value: profile.user_id,
        meta: profile,
        isDisabled: !profile.enabled,
        hintText: !profile.enabled ? "This user is disabled" : undefined,
        name: profile.name,
        email: profile.email,
      }));
  }, [userProfiles, currentOrgMembers, addedMembers, orgId]);

  const roleOptions = useMemo(() => {
    return Object.entries(ROLES)
      .filter(([key]) => orgId === "populi" || !SYSTEM_ROLE_KEYS.includes(key))
      .map(([key, value]) => ({ label: value, value: key }));
  }, [orgId]);

  const ahuRoleOptions = useMemo(() => {
    return Object.entries(ANALYTICS_HUB_USER_ROLES).map(([key, value]) => ({
      label: value,
      value: key,
    }));
  }, []);

  const biRoleOptions = useMemo(() => {
    return Object.entries(TABLEAU_ROLES)
      .filter(([key]) => orgId === "populi" || key !== "creator")
      .map(([key, value]) => ({ label: value, value: key }));
  }, [orgId]);

  const filterByNameOrEmail = useCallback((option, inputValue) => {
    if (!inputValue) return true;

    const search = inputValue.toLowerCase();

    return (
      option.data.name?.toLowerCase().includes(search) ||
      option.data.email?.toLowerCase().includes(search)
    );
  }, []);

  const addMembersMutation = useAuthenticatedMutation(createMemberships);
  const inviteUsersMutation = useAuthenticatedMutation(createUserProfiles);
  const { mutate: addMembers, isPending: isSubmitting } = useMutation({
    mutationFn: async ({ newMembers, newUsers }) => {
      console.log({ newMembers, newUsers });
      const requests = {
        adds: null,
        invites: null,
      };

      if (newMembers.length) {
        requests.adds = addMembersMutation({ memberships: newMembers });
      }

      if (newUsers.length) {
        requests.invites = inviteUsersMutation({ profiles: newUsers });
      }

      const [settledAdds, settledInvites] = await Promise.allSettled([
        requests.adds,
        requests.invites,
      ]);

      console.log({ settledAdds, settledInvites });

      const isResponseSuccess = (response) =>
        [200, 201].includes(response.code);
      const isResponseMixed = (response) => response.code === 207;
      const parseSettledState = ({ name, settledPromise, rejectedError }) => {
        const { status, value } = settledPromise;

        if (status === "rejected") {
          return [
            {
              status: "rejected",
              errorMessage: rejectedError,
            },
          ];
        }

        if (status === "fulfilled" && !value) {
          console.log(`[${name}] had no results`);
          return [];
        }

        if (status === "fulfilled") {
          const isSuccess = isResponseSuccess(value);
          const isMixed = isResponseMixed(value);

          if (isSuccess) {
            return value.results.map((result) => {
              return {
                status: "success",
                email: result.email,
                userId: result.user_id,
              };
            });
          }

          if (isMixed) {
            return value.results.map((result) => {
              return {
                status: isResponseSuccess(result.status) ? "success" : "error",
                email: result.email,
                userId: result.user_id,
                errorMessage: result.error,
              };
            });
          }
        }
      };

      const addMembersState = parseSettledState({
        name: "addMembers",
        rejectedError: "There was an issue adding members.",
        settledPromise: settledAdds,
      });
      const inviteUsersState = parseSettledState({
        name: "inviteUsers",
        rejectedError: "There was an issue inviting users.",
        settledPromise: settledInvites,
      });

      console.log({ addMembersState, inviteUsersState });

      return [...addMembersState, ...inviteUsersState];
    },
    onError: (error, variables, onMutateResult, context) => {
      if (Array.isArray(error)) {
        setError(error[0]);
      } else if (typeof error === "string") {
        setError(error);
      } else {
        setError("There was an issue adding member(s).");
      }
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      console.log("addMembers.onSuccess", { data });

      const fulfilledResults = data.filter(
        (result) => result.status !== "rejected"
      );
      const fulfilledErrorResults = data.filter(
        (result) => result.status === "error"
      );
      const rejectedResults = data.filter(
        (result) => result.status === "rejected"
      );

      // Reset successful memberships changes in form back to action === "none"
      fulfilledResults.forEach((result) => {
        const isAdded = !!addedMembers.find((m) => m.userId === result.userId);
        const isInvited = !!invitedMembers.find(
          (m) => m.email === result.email
        );

        if (isAdded) {
          setAddedMembers((prev) => {
            return prev.map((m) => {
              if (m.userId === result.userId) {
                return {
                  ...m,
                  status: result.status,
                  errorMessage: result.errorMessage,
                };
              }
              return m;
            });
          });
        }

        if (isInvited) {
          setInvitedMembers((prev) => {
            return prev.map((m) => {
              if (m.email === result.email) {
                return {
                  ...m,
                  status: result.status,
                  errorMessage: result.errorMessage,
                };
              }
              return m;
            });
          });
        }
      });

      if (rejectedResults.length > 0) {
        const combinedErrors = rejectedResults.map((r) => r.errorMessage);
        setError(combinedErrors.join("\n"));
      }

      if (rejectedResults.length === 0 && fulfilledErrorResults.length === 0) {
        toast.success("Added member(s) successfully!");
        setOpen(false);
      }

      queryClient.invalidateQueries({
        queryKey: ["memberships", { orgId }],
      });
    },
  });

  const handleAddMembers = () => {
    // Use schema to get the transform
    const newMembers = addMembersSchema.safeParse(addedMembers).data;
    const newUsers = invitedMembersSchema.safeParse(invitedMembers).data;

    addMembers({
      newMembers,
      newUsers,
    });
  };

  const handleSelectUser = (userId) => {
    const matchingUser = userProfiles.find((u) => u.user_id === userId);

    setAddedMembers((prev) => {
      return prev.concat({
        userId,
        orgId,
        name: matchingUser.name,
        email: matchingUser.email,
        role: "orgUser",
        ahuRole: "none",
        biRole: "none",
        dataAccess: false,
      });
    });
  };

  const handleInviteUser = (email) => {
    setInvitedMembers((prev) => {
      return prev.concat({
        email,
        orgId,
        role: "orgUser",
        ahuRole: "none",
        biRole: "none",
        dataAccess: false,
      });
    });
  };

  const handleUpdateMember = (action) => (identifier, updates) => {
    if (action === "invited") {
      setInvitedMembers((prev) => {
        return prev.map((invitedMember) => {
          // Match by user_id for existing users, by email for invited users
          const isMatch = invitedMember.user_id
            ? invitedMember.user_id === identifier
            : invitedMember.email === identifier;
          if (isMatch) {
            return {
              ...invitedMember,
              ...updates,
            };
          }

          return invitedMember;
        });
      });
    }

    if (action === "added") {
      setAddedMembers((prev) => {
        return prev.map((addedMember) => {
          // Match by user_id for existing users, by email for invited users
          const isMatch = addedMember.user_id
            ? addedMember.user_id === identifier
            : addedMember.email === identifier;
          if (isMatch) {
            return {
              ...addedMember,
              ...updates,
            };
          }

          return addedMember;
        });
      });
    }
  };

  const handleRemoveMember = (action) => (index) => {
    if (action === "invited") {
      setInvitedMembers((prev) => {
        return prev.toSpliced(index, 1);
      });
    }

    if (action === "added") {
      setAddedMembers((prev) => {
        return prev.toSpliced(index, 1);
      });
    }
  };

  const isLoading = isFetchingProfiles || isFetchingOrgMembers;

  return (
    <Panel isOpen={isOpen} onOpenChange={setOpen} onCloseRedirectTo="../">
      <PanelHeader>
        <PanelTitle>Add Members</PanelTitle>
        <PanelDescription>
          Users can only be members of organizations that allow their domain
          access.
        </PanelDescription>
      </PanelHeader>
      <PanelContent>
        <Error>{error}</Error>
        <Flex direction="column" gap="12px">
          <Combobox
            id="userSearch"
            name="user-search-nofill"
            autoComplete="one-time-code"
            label="User Search"
            isSearchable
            isDisabled={isSubmitting}
            placeholder="Search by name or email..."
            options={userOptions}
            value=""
            onChange={handleSelectUser}
            openMenuOnClick={false}
            filterOption={filterByNameOrEmail}
            getOptionLabel={(option) => {
              const { label, value, meta } = option;

              return (
                <div>
                  <Typography
                    variant="body"
                    noMargin
                    style={{
                      fontWeight: 700,
                      lineHeight: "20px",
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body" noMargin>
                    {meta.email}
                  </Typography>
                </div>
              );
            }}
            NoOptionsComponent={(props) => {
              const { ContainerComponent, selectProps } = props;
              const searchValue = selectProps.inputValue;
              const isValidEmail = Boolean(
                searchValue && !email()(searchValue) // email validator returns false for valid values
              );

              // Check if email domain matches org's allowed domains
              const emailDomain = isValidEmail
                ? searchValue.split("@")[1]?.toLowerCase()
                : "";
              const isDomainAllowed =
                allowedDomains.length === 0 ||
                allowedDomains.some((d) => d.toLowerCase() === emailDomain);

              if (isValidEmail && isDomainAllowed) {
                return (
                  <div
                    onClick={() => {
                      handleInviteUser(searchValue);
                      props.selectProps.onInputChange("");
                      props.selectProps.onMenuClose();
                    }}
                  >
                    <ContainerComponent {...props} withHoverSelect>
                      <Flex gap="16px" alignItems="center">
                        <Flex gap="6px" alignItems="center">
                          <FontAwesomeIcon
                            icon={faPaperPlane}
                            style={{ marginBottom: "-1px" }}
                          />
                          <Typography
                            variant="body"
                            noMargin
                            style={{
                              fontWeight: 700,
                              lineHeight: "20px",
                              textAlign: "left",
                            }}
                          >
                            Invite
                          </Typography>
                        </Flex>

                        <Typography
                          variant="body"
                          noMargin
                          style={{
                            textAlign: "left",
                          }}
                        >
                          {searchValue}
                        </Typography>
                      </Flex>
                    </ContainerComponent>
                  </div>
                );
              }

              if (isValidEmail && !isDomainAllowed) {
                return (
                  <ContainerComponent {...props}>
                    <Typography
                      variant="body"
                      noMargin
                      style={{
                        fontWeight: 700,
                        lineHeight: "20px",
                        textAlign: "left",
                      }}
                    >
                      Domain not allowed
                    </Typography>
                    <Typography
                      variant="body"
                      noMargin
                      style={{
                        textAlign: "left",
                        marginTop: "4px",
                      }}
                    >
                      Allowed domains:
                    </Typography>
                    <ul
                      style={{
                        margin: "4px 0 0 0",
                        paddingLeft: "20px",
                        listStyleType: "disc",
                      }}
                    >
                      {allowedDomains.map((domain) => (
                        <li key={domain}>
                          <Typography
                            variant="body"
                            noMargin
                            style={{ textAlign: "left" }}
                          >
                            {domain}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </ContainerComponent>
                );
              }

              return (
                <ContainerComponent {...props}>
                  <Typography
                    variant="body"
                    noMargin
                    style={{
                      fontWeight: 700,
                      lineHeight: "20px",
                      textAlign: "left",
                    }}
                  >
                    No matches found
                  </Typography>
                  <Typography
                    variant="body"
                    noMargin
                    style={{
                      textAlign: "left",
                    }}
                  >
                    Try using their email instead
                  </Typography>
                  {allowedDomains.length > 0 && (
                    <>
                      <Typography
                        variant="body"
                        noMargin
                        style={{
                          textAlign: "left",
                          marginTop: "12px",
                        }}
                      >
                        Allowed domains:
                      </Typography>
                      <ul
                        style={{
                          margin: "4px 0 0 0",
                          paddingLeft: "20px",
                          listStyleType: "disc",
                        }}
                      >
                        {allowedDomains.map((domain) => (
                          <li key={domain}>
                            <Typography
                              variant="body"
                              noMargin
                              style={{ textAlign: "left" }}
                            >
                              {domain}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </ContainerComponent>
              );
            }}
            showSkeleton={isLoading}
            icons={{
              openMenu: faSearch,
            }}
          />

          {addedMembers.map((member, index) => {
            return (
              <MembershipCard
                key={member.user_id || member.email}
                status={member.status}
                dataTest={`membership-card-${member.user_id || member.email}`}
              >
                <Flex
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Flex direction="row" alignItems="center">
                    <Typography variant="h6" noMargin>
                      {member.name || member.email}
                    </Typography>
                    {!!member.name && (
                      <>
                        <FontAwesomeIcon
                          id={`memberEmail-${index}`}
                          icon={faEnvelope}
                          style={{
                            color: "#000",
                            marginLeft: "4px",
                            marginBottom: "1px",
                          }}
                        />
                        <Tooltip
                          anchorSelect={`#memberEmail-${index}`}
                          children={member.email}
                          place="right"
                          positionStrategy="absolute"
                          offset={10}
                          style={{
                            zIndex: "10000",
                            backgroundColor: "#2A0F4D",
                          }}
                        />
                      </>
                    )}
                  </Flex>
                  <Button
                    variant="icon"
                    size="small"
                    onClick={() => handleRemoveMember("added")(index)}
                    style={{ marginRight: "4px" }}
                  >
                    <FontAwesomeIcon icon={faClose} />
                  </Button>
                </Flex>

                <Flex direction="row" gap="8px">
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <Field
                      type="combobox"
                      size="small"
                      id="role"
                      labelText="Role"
                      isRequired
                      isDisabled={isSubmitting}
                      hideError={true}
                      options={roleOptions}
                      value={member.role}
                      onChange={(value) => {
                        handleUpdateMember("added")(
                          member.user_id || member.email,
                          {
                            role: value,
                          }
                        );
                      }}
                    />
                  </div>

                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <Field
                      type="combobox"
                      size="small"
                      id="analyticsHubUser"
                      labelText="Analytics Hub User"
                      isRequired
                      placeholder="Please Select"
                      isDisabled={isSubmitting}
                      hideError={true}
                      options={ahuRoleOptions}
                      value={member.ahuRole}
                      onChange={(value) => {
                        handleUpdateMember("added")(
                          member.user_id || member.email,
                          {
                            ahuRole: value,
                          }
                        );
                      }}
                    />
                  </div>
                </Flex>

                <Flex direction="row" gap="8px">
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <Field
                      type="combobox"
                      id="biPortalUser"
                      size="small"
                      labelText="BI Role"
                      isRequired
                      isDisabled={isSubmitting}
                      hideError={true}
                      placeholder="Please Select"
                      options={biRoleOptions}
                      value={member.biRole}
                      onChange={(value) => {
                        handleUpdateMember("added")(
                          member.user_id || member.email,
                          {
                            biRole: value,
                          }
                        );
                      }}
                    />
                  </div>

                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <Field
                      type="combobox"
                      id="directDataAccess"
                      size="small"
                      labelText="Data Access"
                      isRequired
                      isDisabled={isSubmitting}
                      hideError={true}
                      options={[
                        { label: "Enabled", value: true },
                        { label: "Disabled", value: false },
                      ]}
                      value={member.dataAccess}
                      onChange={(value) => {
                        handleUpdateMember("added")(
                          member.user_id || member.email,
                          {
                            dataAccess: value,
                          }
                        );
                      }}
                    />
                  </div>
                </Flex>
                {member.errorMessage && (
                  <MembershipCardError>
                    {member.errorMessage}
                  </MembershipCardError>
                )}
              </MembershipCard>
            );
          })}
          {invitedMembers.map((member, index) => {
            return (
              <MembershipCard
                key={member.email}
                status={member.status}
                dataTest={`membership-card-${member.email}`}
              >
                <Flex
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" noMargin>
                    {member.name || member.email}
                  </Typography>
                  <Button
                    variant="icon"
                    size="small"
                    onClick={() => handleRemoveMember("invited")(index)}
                    style={{ marginRight: "4px" }}
                  >
                    <FontAwesomeIcon icon={faClose} />
                  </Button>
                </Flex>

                <Flex direction="row" gap="8px">
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <Field
                      type="combobox"
                      size="small"
                      id="role"
                      labelText="Role"
                      isRequired
                      isDisabled={isSubmitting}
                      hideError={true}
                      options={roleOptions}
                      value={member.role}
                      onChange={(value) => {
                        handleUpdateMember("invited")(
                          member.user_id || member.email,
                          {
                            role: value,
                          }
                        );
                      }}
                    />
                  </div>

                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <Field
                      type="combobox"
                      size="small"
                      id="analyticsHubUser"
                      labelText="Analytics Hub User"
                      isRequired
                      placeholder="Please Select"
                      isDisabled={isSubmitting}
                      hideError={true}
                      options={ahuRoleOptions}
                      value={member.ahuRole}
                      onChange={(value) => {
                        handleUpdateMember("invited")(
                          member.user_id || member.email,
                          {
                            ahuRole: value,
                          }
                        );
                      }}
                    />
                  </div>
                </Flex>

                <Flex direction="row" gap="8px">
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <Field
                      type="combobox"
                      id="biPortalUser"
                      size="small"
                      labelText="BI Role"
                      isRequired
                      isDisabled={isSubmitting}
                      hideError={true}
                      placeholder="Please Select"
                      options={biRoleOptions}
                      value={member.biRole}
                      onChange={(value) => {
                        handleUpdateMember("invited")(
                          member.user_id || member.email,
                          {
                            biRole: value,
                          }
                        );
                      }}
                    />
                  </div>

                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <Field
                      type="combobox"
                      id="directDataAccess"
                      size="small"
                      labelText="Data Access"
                      isRequired
                      isDisabled={isSubmitting}
                      hideError={true}
                      options={[
                        { label: "Enabled", value: true },
                        { label: "Disabled", value: false },
                      ]}
                      value={member.dataAccess}
                      onChange={(value) => {
                        handleUpdateMember("invited")(
                          member.user_id || member.email,
                          {
                            dataAccess: value,
                          }
                        );
                      }}
                    />
                  </div>
                </Flex>
                {member.errorMessage && (
                  <MembershipCardError>
                    {member.errorMessage}
                  </MembershipCardError>
                )}
              </MembershipCard>
            );
          })}

          {/* Action buttons */}
          <Flex direction="row" gap="16px" justifyContent="flex-end">
            <Button
              variant="outlined"
              width="auto"
              disabled={isSubmitting}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              width="auto"
              onClick={handleAddMembers}
              isLoading={isSubmitting}
              disabled={
                addedMembers.concat(invitedMembers).length === 0 || isSubmitting
              }
            >
              {addedMembers.concat(invitedMembers).length > 1
                ? "Add Members"
                : "Add Member"}
            </Button>
          </Flex>
        </Flex>
      </PanelContent>
    </Panel>
  );
};

export default AddMemberPanel;
